import { Component, EventEmitter, Output } from '@angular/core';
import {
	AdminConnexDriverAssignmentSlot,
	AdminXpressConnexService,
	ConnexDriverAvailabilityStatusEnum,
	DriverStaffingStatusEnum,
	emptyBaseAdminRequest
} from 'app/services';
import { Utils } from 'app/utils';
import { MessageService } from 'primeng/api';
import { SelectItem } from 'primeng/api';

export type ManageDriversAvailability = 'available' | 'assigned' | 'unavailable';

export interface ManageDriversDrawerRow {
	id: number;
	name: string;
	vehicleModel: string;
	mobile: string;
	plateNumber: string;
	profileImageUrl: string;
	photoLoadFailed: boolean;
	availability: ManageDriversAvailability;
	isAssignedToSlot: boolean;
	selected: boolean;
	hasOverlap: boolean;
	overlapMessage?: string;
}

export interface ManageDriversOverlapConflict {
	driverName: string;
	slotDescription: string;
}

@Component({
	selector: 'connex-manage-drivers-drawer',
	templateUrl: './connex-manage-drivers-drawer.component.html',
	styleUrls: ['./connex-manage-drivers-drawer.component.scss']
})
export class ConnexManageDriversDrawerComponent {
	@Output()
	public assignmentsSaved = new EventEmitter<void>();

	public readonly panelStyle: Record<string, string> = { width: '712px' };

	public visible = false;
	public loading = false;
	public saving = false;
	public overlapDialogVisible = false;
	public searchQuery = '';
	public vehicleModelFilter: string | null = null;
	public statusFilter: string[] = [];

	public vehicleModelOptions: SelectItem<string | null>[] = [
		{ label: 'All vehicle models', value: null }
	];

	public readonly statusOptions: SelectItem<string>[] = [
		{ label: 'Available', value: 'available' },
		{ label: 'Assigned', value: 'assigned' },
		{ label: 'Unavailable', value: 'unavailable' }
	];

	public slotContext: {
		slotId: number;
		serviceDate: Date;
		routeName: string;
		departureLabel: string;
		directionLabel: string;
		staffingStatusLabel: string;
		staffingStatus: DriverStaffingStatusEnum;
	} | null = null;

	public drivers: ManageDriversDrawerRow[] = [];
	public overlapConflicts: ManageDriversOverlapConflict[] = [];

	constructor(
		private adminConnexService: AdminXpressConnexService,
		private messageService: MessageService
	) {}

	public get dateLabel(): string {
		if (!this.slotContext) {
			return '—';
		}
		return this.slotContext.serviceDate.toLocaleDateString('en-US', {
			month: 'long',
			day: 'numeric',
			year: 'numeric'
		});
	}

	public get staffingTagSeverity(): 'danger' | 'success' | 'warning' | 'secondary' | 'info' {
		switch (this.slotContext?.staffingStatus) {
			case DriverStaffingStatusEnum.Ready:
				return 'success';
			case DriverStaffingStatusEnum.PartiallyStaffed:
				return 'warning';
			case DriverStaffingStatusEnum.NoDriversAssigned:
				return 'danger';
			default:
				return 'secondary';
		}
	}

	public get filteredDrivers(): ManageDriversDrawerRow[] {
		const q = this.searchQuery.trim().toLowerCase();
		return this.drivers.filter((d) => {
			if (q) {
				const haystack = `${d.name} ${d.mobile} ${d.plateNumber}`.toLowerCase();
				if (!haystack.includes(q)) {
					return false;
				}
			}

			if (
				this.vehicleModelFilter &&
				d.vehicleModel !== this.vehicleModelFilter
			) {
				return false;
			}

			if (this.statusFilter.length > 0) {
				const rowStatus = this.driverRowStatus(d);
				if (!this.statusFilter.includes(rowStatus)) {
					return false;
				}
			}

			return true;
		});
	}

	public get selectedCount(): number {
		return this.drivers.filter((d) => d.selected).length;
	}

	public get shownCount(): number {
		return this.filteredDrivers.length;
	}

	public open(slot: AdminConnexDriverAssignmentSlot, serviceDate: Date): void {
		const departureLabel = this.formatDepartureLabel(slot.DepartureAt);

		this.slotContext = {
			slotId: slot.SlotId,
			serviceDate: new Date(serviceDate),
			routeName: slot.RouteName,
			departureLabel,
			directionLabel: slot.PickupZoneName,
			staffingStatusLabel: this.staffingStatusLabel(slot.StaffingStatus),
			staffingStatus: slot.StaffingStatus
		};

		this.searchQuery = '';
		this.vehicleModelFilter = null;
		this.vehicleModelOptions = [{ label: 'All vehicle models', value: null }];
		this.statusFilter = [];
		this.overlapDialogVisible = false;
		this.overlapConflicts = [];
		this.visible = true;
		void this.loadDrivers();
	}

	private formatDepartureLabel(departureAt: string): string {
		const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(departureAt ?? '');
		if (!match) {
			return '—';
		}

		const hour = Number(match[4]);
		const minute = Number(match[5]);
		if (Number.isNaN(hour) || Number.isNaN(minute)) {
			return '—';
		}

		return new Date(2000, 0, 1, hour, minute)
			.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
	}

	private staffingStatusLabel(status: DriverStaffingStatusEnum): string {
		switch (status) {
			case DriverStaffingStatusEnum.NoDriversAssigned:
				return 'No Drivers Assigned';
			case DriverStaffingStatusEnum.PartiallyStaffed:
				return 'Partially Staffed';
			case DriverStaffingStatusEnum.Ready:
				return 'Ready';
			default:
				return '';
		}
	}

	public close(): void {
		this.visible = false;
		this.overlapDialogVisible = false;
	}

	public async onSave(): Promise<void> {
		if (!this.slotContext || this.saving) {
			return;
		}

		const selected = this.drivers.filter((d) => d.selected);
		if (selected.length === 0) {
			await this.persistAssignments();
			return;
		}

		const conflicts = this.buildOverlapConflicts(selected);
		if (conflicts.length > 0) {
			this.overlapConflicts = conflicts;
			this.overlapDialogVisible = true;
			return;
		}

		await this.persistAssignments();
	}

	public cancelOverlapDialog(): void {
		this.overlapDialogVisible = false;
	}

	public async confirmOverlapAddAnyway(): Promise<void> {
		this.overlapDialogVisible = false;
		await this.persistAssignments();
	}

	public availabilityLabel(driver: ManageDriversDrawerRow): string {
		switch (this.driverRowStatus(driver)) {
			case 'available':
				return 'Available';
			case 'assigned':
				return 'Assigned';
			case 'unavailable':
				return 'Unavailable';
			default:
				return '';
		}
	}

	public availabilityTagClass(driver: ManageDriversDrawerRow): string {
		switch (this.driverRowStatus(driver)) {
			case 'available':
				return 'cmd-driver-row__status--available';
			case 'assigned':
				return 'cmd-driver-row__status--assigned';
			case 'unavailable':
				return 'cmd-driver-row__status--unavailable';
			default:
				return '';
		}
	}

	public driverInitials(name: string): string {
		const parts = name.trim().split(/\s+/).filter(Boolean);
		if (parts.length === 0) {
			return '?';
		}
		if (parts.length === 1) {
			return parts[0].slice(0, 2).toUpperCase();
		}
		return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
	}

	public isDriverDisabled(driver: ManageDriversDrawerRow): boolean {
		if (driver.isAssignedToSlot) {
			return false;
		}

		return driver.availability === 'unavailable';
	}

	public onDriverPhotoError(driver: ManageDriversDrawerRow): void {
		driver.photoLoadFailed = true;
	}

	public overlapConflictMessage(conflict: ManageDriversOverlapConflict): string {
		return `${conflict.driverName} is already assigned to "${conflict.slotDescription}". Add them anyway?`;
	}

	public driverRowStatus(driver: ManageDriversDrawerRow): ManageDriversAvailability {
		return driver.isAssignedToSlot ? 'assigned' : driver.availability;
	}

	private buildOverlapConflicts(selected: ManageDriversDrawerRow[]): ManageDriversOverlapConflict[] {
		return selected
			.filter((d) => d.hasOverlap)
			.map((d) => ({
				driverName: d.name,
				slotDescription: this.formatOverlapSlotDescription(d.overlapMessage)
			}));
	}

	private formatOverlapSlotDescription(overlapMessage?: string): string {
		if (!overlapMessage) {
			return '';
		}

		const prefix = 'Already assigned to ';
		if (overlapMessage.toLowerCase().startsWith(prefix.toLowerCase())) {
			return overlapMessage.slice(prefix.length).trim();
		}

		return overlapMessage;
	}

	private async persistAssignments(): Promise<void> {
		if (!this.slotContext || this.saving) {
			return;
		}

		this.saving = true;
		try {
			const selectedIds = this.drivers.filter((d) => d.selected).map((d) => d.id);

			const res = await this.adminConnexService.saveConnexSlotDriverAssignments(
				{
					...emptyBaseAdminRequest(),
					SlotId: this.slotContext.slotId,
					ServiceDate: Utils.formatPHCalendarDate(this.slotContext.serviceDate),
					XpressRiderIds: selectedIds
				},
				this.messageService
			);

			if (!res || res.Success === false) {
				this.messageService.add({
					severity: 'error',
					summary: 'Save failed',
					detail: res?.ErrorMessage ?? 'Unable to save driver assignments'
				});
				return;
			}

			this.messageService.add({
				severity: 'success',
				summary: 'Drivers Assigned Successfully',
				detail: `${res.AssignedDriverCount} driver${res.AssignedDriverCount === 1 ? '' : 's'} assigned to this slot.`
			});

			this.close();
			this.assignmentsSaved.emit();
		} finally {
			this.saving = false;
		}
	}

	private async loadDrivers(): Promise<void> {
		if (!this.slotContext) {
			return;
		}

		this.loading = true;
		try {
			const res = await this.adminConnexService.getConnexSlotDriverAssignments(
				{
					...emptyBaseAdminRequest(),
					SlotId: this.slotContext.slotId,
					ServiceDate: Utils.formatPHCalendarDate(this.slotContext.serviceDate)
				},
				this.messageService
			);

			if (!res?.Success) {
				this.drivers = [];
				return;
			}

			this.drivers = (res.Items ?? []).map((item) => ({
				id: item.XpressRiderId,
				name: item.Name,
				vehicleModel: item.VehicleModel,
				mobile: item.Mobile,
				plateNumber: item.PlateNumber,
				profileImageUrl: item.ProfileImageUrl ?? '',
				photoLoadFailed: false,
				availability: this.mapAvailability(item.AvailabilityStatus),
				isAssignedToSlot: item.IsAssigned,
				selected: item.IsAssigned,
				hasOverlap: item.HasOverlap,
				overlapMessage: item.OverlapMessage ?? undefined
			}));
			this.rebuildVehicleModelOptions();
		} finally {
			this.loading = false;
		}
	}

	private rebuildVehicleModelOptions(): void {
		const models = new Set<string>();
		for (const driver of this.drivers) {
			const model = driver.vehicleModel?.trim();
			if (model) {
				models.add(model);
			}
		}

		const sorted = Array.from(models).sort((a, b) =>
			a.localeCompare(b, undefined, { sensitivity: 'base' })
		);

		this.vehicleModelOptions = [
			{ label: 'All vehicle models', value: null },
			...sorted.map((model) => ({ label: model, value: model }))
		];
	}

	private mapAvailability(status: ConnexDriverAvailabilityStatusEnum): ManageDriversAvailability {
		switch (status) {
			case ConnexDriverAvailabilityStatusEnum.Available:
				return 'available';
			case ConnexDriverAvailabilityStatusEnum.Assigned:
				return 'assigned';
			default:
				return 'unavailable';
		}
	}

}
